import React, { useCallback, useEffect, useState } from 'react';
import type { FloatingButton } from 'shared/src/api/promotionsService/getPromotionComponents/types';
import {
  BadgeNew,
  Colors,
  Flex,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import {
  getRemainTime,
  getRemainTimeUnit,
  isNil,
  isNotNil,
  localStorageBridge,
} from 'shared/src/utils';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useClickFloatingButton } from '../hooks/useClickFloatingButton';
import { useForegroundInterval } from 'shared/src/hooks';

const BUTTON_SIZE = 48;

const FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY =
  `FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY_` as const;

export const PromotionFloatingButton = (props: {
  button: FloatingButton;
  buttonIdx: number;
  promotionSno?: number;
}) => {
  const [isClickOnce, setIsClickOnce] = useState(false);
  const [isShowText, setIsShowText] = useState(true);
  const [remainTime, setRemainTime] = useState('');
  const { button, buttonIdx, promotionSno } = props;
  const { openDeeplink } = useOpenDeeplink();
  const logClickFloatingButton = useClickFloatingButton(
    props.button,
    buttonIdx,
  );
  // new 여부는 localstorage에 저장하기
  // 클릭시 로깅 추가
  const onPressButton = useCallback(() => {
    logClickFloatingButton();
    setIsClickOnce(true);

    localStorageBridge.putBoolean(
      `${FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY}${promotionSno}`,
      true,
    );

    openDeeplink(button.item.deeplink);
  }, [button.item.deeplink, logClickFloatingButton, promotionSno, openDeeplink]);

  const checkClickOncePromotion = useCallback(async () => {
    if (isNil(promotionSno)) {
      return;
    }
    const value = isNotNil(
      await localStorageBridge.getBoolean(
        `${FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY}${promotionSno}`,
      ),
    );
    setIsClickOnce(value);
  }, [promotionSno]);

  useEffect(() => {
    checkClickOncePromotion();
  }, [checkClickOncePromotion]);

  const { timer } = button.item.render;

  const remainTimeUnit = isNotNil(timer)
    ? getRemainTimeUnit(timer.expired_at)
    : null;

  const remainTimeDelay = (() => {
    switch (remainTimeUnit) {
      case 'minute':
        return 1000 * 60;
      case 'second':
        return 1000;
      default:
        return null;
    }
  })();

  // 텍스트 토글 (3초)
  useForegroundInterval(
    () => setIsShowText(prev => !prev),
    isNotNil(timer) ? 3000 : null,
    { immediate: false },
  );

  // 남은 시간 업데이트
  useForegroundInterval(() => {
    if (isNotNil(timer)) {
      const time = getRemainTime(timer.expired_at);
      setRemainTime(`${time} 남음`);
    }
  }, remainTimeDelay);

  return (
    <Flex
      width={BUTTON_SIZE}
      height={BUTTON_SIZE}
      borderRadius={16}
      borderWidth={1}
      alignItems={'center'}
      justifyContent={'center'}
      shadowStep={'medium'}
      borderColorValue={
        button.item.render.border_color ?? Colors.border_secondary
      }
      backgroundColorValue={button.item.render.background_color}
      onPress={onPressButton}
    >
      <RemoteImage uri={button.item.render.icon} width={20} height={20} />
      {!isShowText && isNotNil(button.item.render.timer) ? (
        <Typography
          type={'subtitle7'}
          numberOfLines={1}
          rawColor={button.item.render.timer.text_color}
        >
          {remainTime}
        </Typography>
      ) : (
        <Typography
          type={'subtitle7'}
          numberOfLines={1}
          rawColor={button.item.render.text_color}
        >
          {button.item.render.text}
        </Typography>
      )}
      {!isClickOnce && (
        <Flex position={'absolute'} top={0} right={0}>
          <BadgeNew />
        </Flex>
      )}
    </Flex>
  );
};
